import { useEffect, useState, useCallback } from 'react'
import dashboardService from '../../../services/dashboard-service'
import GrileService from '../../../services/grile-service'
import {
  getLast7DaysInterval,
  getLast7DaysOfWeek,
  getLast12Months,
  getLastFullYearBoundsFromCurrentDate,
  circularRightShift,
  getCorrectSolvedGrids,
  isAnswerCorrect,
} from '../../../utils'
import useGetFilters from '../../../hooks/useGetAllTags'

const useDashboard = (period, chapter, selectedCourse) => {
  const { coursesWithChapters, handleGetChaptersByCourse, chapters } =
    useGetFilters()

  const handleGetWhichCourseIsChapter = useCallback(
    chapter => {
      for (const course in coursesWithChapters) {
        for (const capitol of coursesWithChapters[course]) {
          if (capitol.name === chapter) {
            return course
          }
        }
      }
    },
    [coursesWithChapters],
  )

  const [data, setData] = useState({
    gridsSolved: null,
    subjectSolved: null,
    hintsUsed: null,
    averageTime: '',
    hitsUsed: null,
    pieChartData: [],
    barChart: {
      xAxis: [],
      subjectsValue: {
        correctSubjects: [],
        incorrectSubjects: [],
      },
      gridsValue: {
        correctGrids: [],
        incorrectGrids: [],
      },
    },
    subjectAverageCorrect: '',
    correctGridsSolved: {
      percent: '',
      value: null,
    },
  })
  const [isLoading, setIsLoading] = useState(false)
  const [grids, setGrids] = useState([])
  const [subjects, setSubjects] = useState([])
  const [averages, setAverages] = useState(null)

  useEffect(() => {
    const fetcher = async () => {
      try {
        setIsLoading(true)

        const response = await dashboardService.answers()
        setGrids(response?.answers)
        setAverages({
          weekly: response?.weeklyAverage?.map(day => day?.average.toFixed(0)),
          monthly: response?.monthlyAverage?.map(month =>
            month?.average.toFixed(0),
          ),
        })

        const subjects = await GrileService.subjectHistory()
        setSubjects(subjects)
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
      }
    }
    fetcher()
  }, [])

  useEffect(() => {
    const currentDay = new Date().getDay()
    const currentMonthIndex = new Date().getMonth()
    const last12MonthsInterval = getLastFullYearBoundsFromCurrentDate()
    const last7DaysGridsInterval = getLast7DaysInterval()
    const selectedPeriod =
      period === 'weekly' ? last7DaysGridsInterval : last12MonthsInterval
    const allGridsFromSubjects = subjects.map(subject => subject.grile).flat()
    const allGrids = [...grids, ...allGridsFromSubjects]

    const handleFilterGridsByCourse = course => {
      const tags = coursesWithChapters?.[course]?.map(chapter => chapter.id)
      return grids.filter(grid => {
        return tags?.some(tag => grid?.tags?.includes(tag))
      })
    }

    const handleFilterSubjectsGridsByCourse = course => {
      const tags = coursesWithChapters?.[course]?.map(chapter => chapter.id)
      return (
        subjects?.flatMap(subject =>
          subject?.grile
            ?.filter(grid => {
              return tags?.some(tag => grid?.tags?.includes(tag))
            })
            .map(grid => ({
              grid,
              timestamp: subject.timestamp,
            })),
        ) ?? []
      )
    }

    const filteredGridsByCourse = handleFilterGridsByCourse(selectedCourse)
    const filteredSubjectsGridsByCourse =
      handleFilterSubjectsGridsByCourse(selectedCourse)
    const filteredGridsByCourseAndPeriod = filteredGridsByCourse.filter(
      grid =>
        grid.timestamp * 1000 >= selectedPeriod.start &&
        grid.timestamp * 1000 <= selectedPeriod.end,
    )
    const filteredSubjectsGridsByCourseAndPeriod =
      filteredSubjectsGridsByCourse.filter(
        grid =>
          grid.timestamp * 1000 >= selectedPeriod.start &&
          grid.timestamp * 1000 <= selectedPeriod.end,
      )

    const chaptersByCourse = handleGetChaptersByCourse(selectedCourse)

    const chapterTag =
      selectedCourse === 'all' && chapter === 'all'
        ? chapters?.map(chapter => chapter.id)
        : chapter === 'all' && selectedCourse !== 'all'
          ? chaptersByCourse?.map(chapter => chapter.id)
          : selectedCourse === 'all' && chapter !== 'all'
            ? [
                coursesWithChapters[
                  handleGetWhichCourseIsChapter(chapter)
                ]?.find(mappedChapter => mappedChapter.name === chapter)?.id,
              ]
            : [
                coursesWithChapters[selectedCourse]?.find(
                  mappedChapter => mappedChapter.name === chapter,
                )?.id,
              ]

    // Grids And Subjects filtered
    const filteredGridsByChapter =
      chapterTag?.length === 0
        ? grids
        : grids.filter(grid => {
            return chapterTag?.some(tag => grid?.tags?.includes(tag))
          })
    const filteredSubjectsGridsByChapter =
      chapterTag?.length === 0
        ? (subjects?.flatMap(subject =>
            subject?.grile?.map(grid => ({
              grid,
              timestamp: subject.timestamp,
            })),
          ) ?? [])
        : (subjects?.flatMap(subject =>
            subject?.grile
              ?.filter(grid => {
                return chapterTag?.some(tag => grid?.tags?.includes(tag))
              })
              .map(grid => ({
                grid,
                timestamp: subject.timestamp,
              })),
          ) ?? [])

    const filteredGridsByPeriodAndChapter = filteredGridsByChapter.filter(
      grid =>
        grid.timestamp * 1000 >= selectedPeriod.start &&
        grid.timestamp * 1000 <= selectedPeriod.end,
    )

    const filteredGridsByPeriod = grids.filter(
      grid =>
        grid.timestamp * 1000 >= selectedPeriod.start &&
        grid.timestamp * 1000 <= selectedPeriod.end,
    )
    const filteredSubjectsGridsByPeriodAndChapter =
      filteredSubjectsGridsByChapter.filter(
        grid =>
          grid.timestamp * 1000 >= selectedPeriod.start &&
          grid.timestamp * 1000 <= selectedPeriod.end,
      )

    const filteredSubjectsByPeriod = subjects.filter(
      subject =>
        subject.timestamp * 1000 >= selectedPeriod.start &&
        subject.timestamp * 1000 <= selectedPeriod.end,
    )

    const filteredSubjectsGridsByPeriod = filteredSubjectsByPeriod
      ?.map(subject => subject?.grile)
      .flat()

    // Chapter you should work
    let correctGridsSolvedByChapter = []
    const chaptersToLoop =
      selectedCourse === 'all' ? chapters : chaptersByCourse

    if (grids?.length && chaptersToLoop?.length) {
      correctGridsSolvedByChapter = chaptersToLoop.map(filter => ({
        chapter: filter?.name
          ? filter.name.charAt(0).toUpperCase() + filter.name.slice(1)
          : '',
        count: 0,
        allGrids: 0,
      }))

      allGrids?.forEach(grid => {
        const subtractValue =
          selectedCourse === 'all' ? 1 : chaptersByCourse[0].id
        grid?.tags?.forEach(tag => {
          if (correctGridsSolvedByChapter[tag - subtractValue]) {
            ++correctGridsSolvedByChapter[tag - subtractValue].allGrids
          }
        })

        if (
          grid?.choice === grid?.answer ||
          grid?.choice === grid?.correct_answer
        ) {
          grid.tags?.forEach(tag => {
            if (correctGridsSolvedByChapter[tag - subtractValue]) {
              ++correctGridsSolvedByChapter[tag - subtractValue].count
            }
          })
        }
      })
    }

    // Hints
    const totalHintsUsedForGrids = filteredGridsByPeriodAndChapter?.reduce(
      (acc, curr) => {
        if (Number(curr?.hints) !== 0) {
          return acc + 1
        } else {
          return acc
        }
      },
      0,
    )

    const totalHintsFromSubjectsUsed =
      filteredSubjectsGridsByPeriodAndChapter?.reduce((acc, curr) => {
        if (curr.grid.hint) {
          return acc + 1
        } else {
          return acc
        }
      }, 0)

    const chapterYouShouldWork =
      correctGridsSolvedByChapter.reduce((prev, curr) => {
        if (curr.allGrids > 0) {
          if (prev === null) {
            return curr
          }

          const prevPercent = prev.count / prev.allGrids
          const currPercent = curr.count / curr.allGrids

          if (
            currPercent < prevPercent ||
            (currPercent === prevPercent && curr.allGrids > prev.allGrids)
          ) {
            return curr
          }
        }

        return prev
      }, null)?.chapter || '-'

    // Average Time per grid and total number of + percent
    const totalSolvingTime = filteredGridsByPeriodAndChapter?.reduce(
      (acc, curr) => {
        if (curr && typeof curr.solving_time === 'number') {
          return acc + curr.solving_time
        } else {
          return acc
        }
      },
      0,
    )

    const totalNumberOfGridsFromSubjects = filteredSubjectsByPeriod?.reduce(
      (acc, curr) => {
        return acc + curr?.grile?.length
      },
      0,
    )

    const totalSolvingTimeForSubjects = filteredSubjectsByPeriod?.reduce(
      (acc, curr) => {
        if (
          curr &&
          typeof curr.solving_time === 'number' &&
          curr.solving_time !== 0
        ) {
          return acc + curr.solving_time
        } else {
          return acc
        }
      },
      0,
    )

    const correctSolvedGrids = getCorrectSolvedGrids(
      filteredGridsByPeriodAndChapter,
    )
    const correctSolvedGridsFromSubjects = getCorrectSolvedGrids(
      filteredSubjectsGridsByPeriodAndChapter.map(grid => grid.grid),
    )

    const percentageCorrect =
      ((correctSolvedGrids?.length + correctSolvedGridsFromSubjects.length) /
        (filteredSubjectsGridsByPeriodAndChapter?.length +
          filteredGridsByPeriodAndChapter.length)) *
      100

    // Average for subjects
    let totalPercent = 0

    filteredSubjectsByPeriod?.forEach(subject => {
      const correctGridsSolved = getCorrectSolvedGrids(subject?.grile)
      const numberOfGrids = subject?.grile?.length
      const percentCorrectPerSubject =
        (correctGridsSolved?.length / numberOfGrids) * 100
      totalPercent += percentCorrectPerSubject
    })

    const averagePercentPerSubject =
      filteredSubjectsByPeriod?.length > 0
        ? totalPercent / filteredSubjectsByPeriod?.length
        : 0

    // BAR CHART Data manipulation for the last 7 days

    const last7DaysGrids = filteredGridsByChapter.filter(grid => {
      const timestamp = grid.timestamp * 1000
      return (
        timestamp >= Date.parse(last7DaysGridsInterval?.start) &&
        timestamp <= Date.parse(last7DaysGridsInterval?.end)
      )
    })

    const weeklyCorrectGridsUnsorted = Array(7).fill(0)
    const weeklyIncorrectGridsUnsorted = Array(7).fill(0)

    last7DaysGrids.forEach(grid => {
      const gridTimestamp = new Date(grid.timestamp * 1000).getDay()

      const correctAnswer = isAnswerCorrect(grid.choice, grid.answer)

      if (correctAnswer) {
        ++weeklyCorrectGridsUnsorted[gridTimestamp]
      } else {
        ++weeklyIncorrectGridsUnsorted[gridTimestamp]
      }
    })

    const weeklyCorrectGrids = circularRightShift(
      weeklyCorrectGridsUnsorted,
      6 - currentDay,
    )

    const weeklyIncorrectGrids = circularRightShift(
      weeklyIncorrectGridsUnsorted,
      6 - currentDay,
    )

    // BAR CHART Data manipulation for the last 7 days for subjects

    const last7DaysSubjectGrids = filteredSubjectsGridsByChapter.filter(
      grid => {
        const timestamp = grid.timestamp * 1000
        return (
          timestamp >= Date.parse(last7DaysGridsInterval.start) &&
          timestamp <= Date.parse(last7DaysGridsInterval.end)
        )
      },
    )

    const weeklyCorrectSubjectGridsUnsorted = Array(7).fill(0)
    const weeklyIncorrectSubjectGridsUnsorted = Array(7).fill(0)

    last7DaysSubjectGrids.forEach(grid => {
      const gridDay = new Date(grid.timestamp * 1000).getDay()

      const correctAnswer = isAnswerCorrect(
        grid.grid.choice,
        grid.grid.correct_answer,
      )

      if (correctAnswer) {
        ++weeklyCorrectSubjectGridsUnsorted[gridDay]
      } else {
        ++weeklyIncorrectSubjectGridsUnsorted[gridDay]
      }
    })

    const weeklyCorrectSubjectGrids = circularRightShift(
      weeklyCorrectSubjectGridsUnsorted,
      6 - currentDay,
    )

    const weeklyIncorrectSubjectGrids = circularRightShift(
      weeklyIncorrectSubjectGridsUnsorted,
      6 - currentDay,
    )

    // BAR CHART Data manipulation for the last 12 months grids

    const monthlyCorrectGridsUnsorted = Array(12).fill(0)
    const monthlyIncorrectGridsUnsorted = Array(12).fill(0)

    filteredGridsByPeriodAndChapter.forEach(grid => {
      const gridMonth = new Date(grid.timestamp * 1000).getMonth()

      const correctAnswer = isAnswerCorrect(grid.choice, grid.answer)

      if (correctAnswer) {
        ++monthlyCorrectGridsUnsorted[gridMonth]
      } else {
        ++monthlyIncorrectGridsUnsorted[gridMonth]
      }
    })

    const monthlyCorrectGrids = circularRightShift(
      monthlyCorrectGridsUnsorted,
      11 - currentMonthIndex,
    )

    const monthlyIncorrectGrids = circularRightShift(
      monthlyIncorrectGridsUnsorted,
      11 - currentMonthIndex,
    )

    // BAR CHART Data manipulation for the last 12 months subject grids

    const monthlyCorrectSubjectGridsUnsorted = Array(12).fill(0)
    const monthlyIncorrectSubjectGridsUnsorted = Array(12).fill(0)

    filteredSubjectsGridsByPeriodAndChapter.forEach(grid => {
      const gridMonth = new Date(grid.timestamp * 1000).getMonth()

      const correctAnswer = isAnswerCorrect(
        grid.grid.choice,
        grid.grid.correct_answer,
      )

      if (correctAnswer) {
        ++monthlyCorrectSubjectGridsUnsorted[gridMonth]
      } else {
        ++monthlyIncorrectSubjectGridsUnsorted[gridMonth]
      }
    })

    const monthlyCorrectSubjectGrids = circularRightShift(
      monthlyCorrectSubjectGridsUnsorted,
      11 - currentMonthIndex,
    )

    const monthlyIncorrectSubjectGrids = circularRightShift(
      monthlyIncorrectSubjectGridsUnsorted,
      11 - currentMonthIndex,
    )

    // Pie Chart data
    let pieChartData = []
    const chaptersForPieChart =
      selectedCourse === 'all' ? chapters : chaptersByCourse
    if (
      chaptersForPieChart &&
      chaptersForPieChart.length > 0 &&
      grids &&
      grids.length > 0
    ) {
      pieChartData = chaptersForPieChart.map(filter => ({
        chapter: filter.name
          ? filter.name.charAt(0).toUpperCase() + filter.name.slice(1)
          : '',
        count: 0,
      }))
      const arrayToLoop =
        selectedCourse === 'all'
          ? [...filteredGridsByPeriod, ...filteredSubjectsGridsByPeriod]
          : [
              ...filteredGridsByCourseAndPeriod,
              ...filteredSubjectsGridsByCourseAndPeriod,
            ]
      arrayToLoop.forEach(grid => {
        if (grid && grid.tags && grid.tags.length > 0) {
          grid.tags.forEach(tag => {
            const index =
              selectedCourse === 'all' ? tag - 1 : tag - chaptersByCourse[0].id
            if (index >= 0 && index < pieChartData.length) {
              pieChartData[index].count++
            }
          })
        }
      })
    }

    const averageTime = (
      (totalSolvingTime || 0 + totalSolvingTimeForSubjects || 0) /
        filteredGridsByPeriodAndChapter?.length +
      filteredSubjectsGridsByPeriodAndChapter.length
    ).toFixed(0)
    const formatAverageTime =
      !isNaN(averageTime) && averageTime
        ? averageTime >= 60
          ? `${Math.floor(averageTime / 60)}m ${averageTime % 60}s`
          : `${averageTime}s`
        : '-'

    setData({
      gridsSolved:
        filteredGridsByPeriodAndChapter?.length +
        filteredSubjectsGridsByPeriodAndChapter.length,
      subjectSolved: filteredSubjectsByPeriod?.length,
      chapterYouShouldWork,
      hintsUsed: totalHintsUsedForGrids + totalHintsFromSubjectsUsed,
      averageTime: formatAverageTime,
      correctGridsSolved: {
        percent: isNaN(percentageCorrect)
          ? '0%'
          : `${percentageCorrect.toFixed(0)}%`,
        value:
          correctSolvedGrids?.length + correctSolvedGridsFromSubjects?.length,
      },
      averages: period === 'monthly' ? averages?.monthly : averages?.weekly,
      subjectAverageCorrect: `${averagePercentPerSubject?.toFixed(1)}%`,
      barChart: {
        xAxis:
          period === 'monthly'
            ? getLast12Months().monthsArray
            : getLast7DaysOfWeek(),
        subjectsValue: {
          correctSubjects:
            period === 'monthly'
              ? monthlyCorrectSubjectGrids
              : weeklyCorrectSubjectGrids,
          incorrectSubjects:
            period === 'monthly'
              ? monthlyIncorrectSubjectGrids
              : weeklyIncorrectSubjectGrids,
        },
        gridsValue: {
          correctGrids:
            period === 'monthly' ? monthlyCorrectGrids : weeklyCorrectGrids,
          incorrectGrids:
            period === 'monthly' ? monthlyIncorrectGrids : weeklyIncorrectGrids,
        },
      },
      pieChartData: pieChartData.filter(element => element.count !== 0),
    })
  }, [
    period,
    chapter,
    chapters,
    grids,
    subjects,
    selectedCourse,
    averages,
    coursesWithChapters,
    handleGetChaptersByCourse,
    handleGetWhichCourseIsChapter,
  ])

  return { data, isLoading }
}

export default useDashboard
